import { Children, isValidElement, type ReactNode } from 'react';

import { COLUMN_CHART_X_SCALE_TYPE_DEFAULT, INDEX_X_DATA_KEY, LINE_CHART_X_SCALE_TYPE_DEFAULT } from '../../constants';
import type { ChartType } from '../chartTypes';
import type { XScaleType } from '../types/Scale';
import type { XAxisProps } from '../types/XAxisProps';
import type { XDataKey } from '../types/XDataKey';

import { isElement } from './isElement';

/**
 * Configuration extracted from XAxis children props.
 * Used by chart components to initialize provider state.
 */
export interface XAxisConfig {
  /** Key in data objects for x-axis values */
  xDataKey: XDataKey;
  /**
   * Scale type for x-axis positioning.
   * - ColumnChart: always 'band' (prop ignored)
   * - LineChart: from xScaleType prop, else default ('time')
   */
  xScaleType: XScaleType;
  /**
   * Format type for tick label display.
   * Falls back to xScaleType if not explicitly provided.
   * Useful when scale differs from format (e.g., ColumnChart with time data).
   */
  dataType: XScaleType;
  /** Whether an XAxis component is present in the chart children */
  hasXAxis: boolean;
  /**
   * Effective label offset (px) from the axis line to the label center.
   * Mirrors XAxis default logic: 40 when a label is present, 0 otherwise.
   * Used by useAutoMargin to compute the required bottom margin.
   */
  xAxisLabelOffset: number;
}

const isXAxisElement = (child: React.ReactElement) => isElement(child, 'XAxis');

/**
 * Recursively searches children to find XAxis props.
 * Handles fragments and nested children.
 */
function findXAxisProps(children: ReactNode): Partial<XAxisProps> | null {
  let found: Partial<XAxisProps> | null = null;

  const searchChild = (child: ReactNode): void => {
    if (found) {
      return;
    } // Already found

    if (!isValidElement(child)) {
      return;
    }

    if (isXAxisElement(child)) {
      found = child.props as XAxisProps;
      return;
    }

    // Recurse into fragments and other containers
    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);
  return found;
}

/**
 * Extracts XAxis configuration from chart children.
 *
 * This enables unidirectional data flow: chart extracts config from children
 * at initialization time, eliminating the need for XAxis to write to context via useEffect.
 *
 * Fallback logic:
 * - xScaleType: from prop → chart type default (from constants.ts)
 * - dataType: from prop → xScaleType (format defaults to scale type)
 *
 * @param children - React children of the chart component
 * @param chartType - Type of chart ('line' or 'column')
 * @returns Configuration object with xDataKey, xScaleType, and dataType
 *
 * @example
 * ```tsx
 * function LineChart({ children, ... }) {
 *   const { xDataKey, xScaleType, dataType } = useMemo(
 *     () => extractXAxisConfig(children, 'line'),
 *     [children]
 *   );
 *   // Pass to provider...
 * }
 * ```
 */
export function extractXAxisConfig(children: ReactNode, chartType: ChartType): XAxisConfig {
  const xAxisProps = findXAxisProps(children);

  const hasXAxis = xAxisProps !== null;
  const xDataKey = xAxisProps?.dataKey ?? INDEX_X_DATA_KEY;
  // Mirror the XAxis component's labelOffset default: 40 when a label is present, 0 otherwise.
  const xAxisLabelOffset = xAxisProps?.labelOffset ?? (xAxisProps?.label ? 40 : 0);

  // ColumnChart ALWAYS uses band scale for positioning
  // xScaleType prop is ignored for ColumnChart
  if (chartType === 'column') {
    const xScaleType = COLUMN_CHART_X_SCALE_TYPE_DEFAULT;
    // dataType falls back to xScaleType if not provided
    const dataType = xAxisProps?.dataType ?? xScaleType;
    return { xDataKey, xScaleType, dataType, hasXAxis, xAxisLabelOffset };
  }

  // LineChart:
  // - xScaleType from prop, else default from constants
  // - dataType from prop, else xScaleType
  const xScaleType = xAxisProps?.xScaleType ?? LINE_CHART_X_SCALE_TYPE_DEFAULT;
  const dataType = xAxisProps?.dataType ?? xScaleType;

  return { xDataKey, xScaleType, dataType, hasXAxis, xAxisLabelOffset };
}
